// Weather (the game modes spec): every round draws one when it starts (clear, rain or harmattan), the
// same for everyone in it, from the round's seed (never the real clock), so a replay shows the same sky.
// From the second minute each minute may turn it to another, at most once a round, with ten
// seconds of warning. A bought weather never turns. It changes vision and braking for everyone
// alike, so it never favours a side. Pure and deterministic like the rest of the simulation.
import { DAY_CYCLE, DEFAULT_WEATHER, } from '@wahala/config';
import { TICK_MS } from './geometry.js';
import { nextInt, seedRng } from './rng.js';
/** Warning before a turn lands (clouds gather, dust lifts; the HUD says so). */
export const WEATHER_WARNING_MS = 10_000;
/** Night and Day arrive as a dusk or a dawn this long (drawn by the phones; the rules switch at once). */
export const WEATHER_DUSK_MS = 20_000;
const MINUTE = 60_000;
// night is the clock's now (the time of day below), never a weather drawn or turned to
const KINDS = ['day', 'rain', 'harmattan'];
/** Vision factor and braking (1 = normal; rain takes 30% longer to stop) for each weather. */
export const WEATHER_EFFECT = {
    day: { vision: 1, brake: 1 },
    night: { vision: 0.85, brake: 1 },
    rain: { vision: 1, brake: 1 / 1.3 },
    harmattan: { vision: 0.75, brake: 1 },
};
export const weatherConfig = (s) => s.config.weather ?? DEFAULT_WEATHER;
/** One weighted draw over the four, from the odds (integers or not: only their ratio counts). */
function draw(rng, odds, not) {
    const pool = KINDS.filter((k) => k !== not);
    // whole units of 1/1000 so the draw is integer maths on every machine
    const w = pool.map((k) => Math.max(0, Math.round(odds[k] * 1000)));
    const total = w.reduce((a, b) => a + b, 0);
    if (total <= 0)
        return pool[0];
    let pick = nextInt(rng, total);
    for (let i = 0; i < pool.length; i++) {
        pick -= w[i];
        if (pick < 0)
            return pool[i];
    }
    return pool[pool.length - 1];
}
export function createWeather(s) {
    const c = weatherConfig(s);
    const rng = seedRng((s.seed ^ 0x57544852) >>> 0);
    // a night bought or set before night became the clock's plays as a clear sky (the clock brings the dark)
    const kind = c.fixed === 'night' ? 'day' : (c.fixed ?? draw(rng, c.odds));
    return {
        rng,
        kind,
        startedAt: s.now,
        // the first chance to turn is at the end of the second minute
        nextRollAt: s.now + 2 * MINUTE,
        turn: null,
        turned: false,
        fixed: c.fixed !== undefined,
    };
}
/** Each tick: roll for a turn once a minute, and land a turn when its warning is over. */
export function tickWeather(s, holdTurn = false) {
    const w = s.weather;
    if (!w)
        return;
    if (w.turn && s.now >= w.turn.at && !holdTurn) {
        w.kind = w.turn.to;
        w.turn = null;
        return;
    }
    if (w.fixed || w.turned || w.turn || s.now < w.nextRollAt)
        return;
    w.nextRollAt += MINUTE;
    const c = weatherConfig(s);
    if (c.turnOneIn <= 0 || nextInt(w.rng, c.turnOneIn) !== 0)
        return;
    w.turned = true;
    w.turn = { to: draw(w.rng, c.odds, w.kind), at: s.now + WEATHER_WARNING_MS };
}
/** The weather in play now (what vision and braking follow). */
export const weatherNow = (s) => s.weather?.kind ?? 'day';
/** The sky's vision factor: the weather's, then the time of day's on top. */
export const weatherVision = (s) => WEATHER_EFFECT[weatherNow(s)].vision * TIME_VISION[timeNow(s)];
export const weatherBrake = (s) => WEATHER_EFFECT[weatherNow(s)].brake;
// ------------------------------------------------------------ the time of day
/** Vision by the time of day, the same for everyone: dusk closes in a little, night more. */
export const TIME_VISION = { day: 1, evening: 0.93, night: 0.85 };
export const dayCycleOf = (s) => s.config.dayCycle ?? DAY_CYCLE;
/** The time of day after this long on the road. */
export function timeAt(c, playedMs) {
    if (playedMs < c.dayMs)
        return 'day';
    if (playedMs < c.dayMs + c.eveningMs)
        return 'evening';
    return 'night';
}
/** Every round starts in daylight. */
export function createDaylight(s) {
    return { playedMs: 0, time: 'day', changedAt: s.now };
}
/** Each tick on the road: the clock moves on, and the time of day turns when it is due. */
export function tickDaylight(s) {
    const d = s.daylight;
    if (!d)
        return;
    d.playedMs += TICK_MS;
    const t = timeAt(dayCycleOf(s), d.playedMs);
    if (t !== d.time) {
        d.time = t;
        d.changedAt = s.now;
    }
}
export const timeNow = (s) => s.daylight?.time ?? 'day';
/** When the next time of day comes (state.now), or null once it is night. */
export function nextTimeAt(s) {
    const d = s.daylight;
    if (!d || d.time === 'night')
        return null;
    const c = dayCycleOf(s);
    return s.now + (d.time === 'day' ? c.dayMs : c.dayMs + c.eveningMs) - d.playedMs;
}
//# sourceMappingURL=weather.js.map